// Outbound-link policy.
//
// Spam-score hygiene: Pal Stars only links out to Pocketpair / Bushiroad
// official Palworld properties. Third-party news, aggregator, wiki and forum
// links were removed because many rotted into dead or irrelevant articles,
// which reads as low-quality outbound linking to search engines.
//
// Every remaining outbound link is still rendered with rel="nofollow ugc
// noopener noreferrer" so no crawl equity or trust is passed outward.

import type { ExternalLink } from "@/lib/guides-data";

/** Hosts considered official Palworld sources (suffix match, incl. subdomains). */
export const OFFICIAL_HOSTS = [
  "palworld.com",
  "pocketpair.jp",
  "palworld-official-cardgame.com",
  "bushiroad.com",
  "store.steampowered.com",
] as const;

export function isOfficialUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase().replace(/^www\./, "");
    return OFFICIAL_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

/** Official fallbacks used when an article has no official link left in its pool. */
export const OFFICIAL_LINKS: ExternalLink[] = [
  {
    label: "Palworld — official site",
    url: "https://www.palworld.com/",
    note: "Pocketpair's official Palworld site: patch notes, roadmap and news.",
  },
  {
    label: "Pocketpair — official news",
    url: "https://www.pocketpair.jp/news?lang=en",
    note: "Official developer announcements from Pocketpair.",
  },
  {
    label: "Palworld Trading Card Game — official site",
    url: "https://en.palworld-official-cardgame.com/",
    note: "Official Palworld TCG card lists, rules and release schedule.",
  },
  {
    label: "Palworld on Steam",
    url: "https://store.steampowered.com/app/1623730/Palworld/",
    note: "Official store page with the current build and update history.",
  },
];

/**
 * Reduce any authored link pool to official Palworld sources only, falling back
 * to the official pool when nothing official remains.
 */
export function officialLinkPool(pool: ExternalLink[] | undefined): ExternalLink[] {
  const kept = (pool ?? []).filter((l) => isOfficialUrl(l.url));
  return kept.length > 0 ? kept : OFFICIAL_LINKS;
}

/** rel value for every outbound link on the site. */
export const OUTBOUND_REL = "nofollow ugc noopener noreferrer";
